import {
  array,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  union,
  ValiError,
} from 'valibot';
import {
  AlbumItemSchema,
  ArtistItemSchema,
  ArtistViewSchema,
  PlaylistItemSchema,
  SongItemSchema,
} from '../apiSchema.ts';
import { camelToKebab, getOffsetNumber } from '../utils.ts';

export const CatalogArtistsIdViewRequestArgsSchema = pipe(
  object({
    id: string(),
    limit: optional(number()),
    offset: optional(number()),
    view: ArtistViewSchema,
  }),
  transform(({ id, limit, offset, view }) => {
    return {
      id,
      limit,
      offset,
      view: camelToKebab(view),
    };
  }),
);

export type CatalogArtistsIdViewRequestArgs = InferOutput<
  typeof CatalogArtistsIdViewRequestArgsSchema
>;

export const CatalogArtistsIdAPISchema = pipe(
  object({
    data: object({
      next: optional(string()),
      data: array(
        union([
          AlbumItemSchema,
          ArtistItemSchema,
          PlaylistItemSchema,
          SongItemSchema,
        ]),
      ),
    }),
  }),
  transform(({ data }) => {
    const { data: viewData, next } = data;
    const type = viewData[0]?.type;

    return {
      key: `${type}s`,
      albums: type === 'album' ? viewData : undefined,
      artists: type === 'artist' ? viewData : undefined,
      playlists: type === 'playlist' ? viewData : undefined,
      tracks: type === 'track' ? viewData : undefined,
      next: next === undefined ? undefined : getOffsetNumber(next),
      total: undefined, // total is not provided in the catalog response
    };
  }),
);

export type CatalogArtistsIdViewResponse = InferOutput<
  typeof CatalogArtistsIdAPISchema
>;

export const $getCatalogArtistsIdView = async (
  player: MusicKit.MusicKitInstance,
  params: CatalogArtistsIdViewRequestArgs,
): Promise<CatalogArtistsIdViewResponse> => {
  const p = safeParse(
    CatalogArtistsIdViewRequestArgsSchema,
    params,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const { id, limit, offset, view } = p.output;
  const endpoint = `/v1/catalog/${player.storefrontId}/artists/${id}/view/${view}`;

  const response = await player.api.music(
    endpoint,
    {
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {}),
    },
  );

  const parsedResponse = safeParse(
    CatalogArtistsIdAPISchema,
    response,
  );
  if (!parsedResponse.success) {
    throw new ValiError(parsedResponse.issues);
  }
  return parsedResponse.output;
};
